/**
 * Handles page view tracking for Single-Page Applications by adapting to the available tracking library.
 * - If `gtag.js` is present, it sends a `page_view` event directly.
 * - If only GTM's `dataLayer` is present, it pushes a `virtual_page_view` event.
 * - Triggers a Hotjar state change to support heatmaps and recordings.
 * - Pushes a `PAGE.VIEW` event for REA (Real Estate Australia) tracking.
 *
 * This function should be called on initial page load and after every Barba.js
 * transition to ensure all page views are recorded.
 *
 * @param {object} [data] - Optional. Barba's transition data object. If not provided,
 *                          it assumes it's the initial page load and uses `window.location`.
 */
export function trackPageView(data, debug = false) {
  const isBarbaTransition = data && data.next;

  const pagePath = isBarbaTransition ? (data.next.url.path + (data.next.url.search || '')) : (window.location.pathname + window.location.search);
  const pageLocation = isBarbaTransition ? data.next.url.href : window.location.href;
  const pageTitle = document.title;

  // --- Google Analytics / GTM Tracking (Adaptive) ---
  // Prefer gtag if it exists, as it's the direct GA library. Fallback to dataLayer for GTM.
  if (typeof window.gtag === 'function') {
    // This handles sites using gtag.js directly.
    // IMPORTANT: For this to not double-count, the client's gtag config must include { 'send_page_view': false }.
    window.gtag('event', 'page_view', {
      page_location: pageLocation,
      page_path: pagePath,
      page_title: pageTitle,
    });
    if (debug) {
      console.log(`GA Page View Tracked (gtag): path: ${pagePath}`);
    }
  } else if (window.dataLayer && Array.isArray(window.dataLayer)) {
    // This handles sites using Google Tag Manager.
    // IMPORTANT: For this to not double-count, your theme automatically injects {'send_page_view': false} before the GTM script.
    window.dataLayer.push({
      event: 'virtual_page_view',
      page_location: pageLocation,
      page_path: pagePath,
      page_title: pageTitle,
    });
    if (debug) {
      console.log(`GTM Virtual Page View Pushed (dataLayer): path: ${pagePath}`);
    }
  } else {
    // This is not an error, just a note for debugging.
    // console.log('Analytics: Neither gtag nor dataLayer found. Skipping GA/GTM tracking.');
  }

  // --- Hotjar Tracking ---
  if (typeof window.hj === 'function') {
    window.hj('stateChange', pagePath);
    if (debug) {
      console.log(`Hotjar State Change Triggered: path: ${pagePath}`);
    }
  }

  // --- REA (Real Estate Australia) Tracking ---
  if (typeof window.uetq === 'function') {
    window.uetq('track', 'PAGE.VIEW');
    if (debug) {
      console.log(`REA PAGE.VIEW event tracked.`);
    }
  }
}
